<template>
  <section class="ps-step" aria-labelledby="ps-heading" data-testid="profile-step">
    <div class="ps-heading">
      <h2 id="ps-heading" class="ps-title" data-testid="ps-title">Set your profile</h2>
      <p class="ps-subtitle" data-testid="ps-subtitle">{{ subtitle }}</p>
    </div>

    <div class="ps-content">
      <div class="ps-photo">
        <span class="ps-photo-label" data-testid="ps-photo-label">Profile picture (optional)</span>

        <div v-if="hasPhoto" class="ps-photo-actions" data-testid="ps-photo-actions">
          <img
            class="ps-photo-img"
            :src="displayPhoto"
            alt="Profile picture preview"
            data-testid="ps-photo-img"
          />
          <StandardButton
            variant="outline"
            size="small"
            data-testid="ps-replace-photo"
            @click="openFilePicker"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M17.5 8.33C17.5 8.33 15.84 5.35 14.34 3.84C12.84 2.34 10.76 1.67 8.74 2.02C6.71 2.37 4.93 3.69 3.99 5.51M2.5 11.67C2.5 11.67 4.16 14.65 5.66 16.16C7.16 17.66 9.24 18.33 11.26 17.98C13.29 17.63 15.07 16.31 16.01 14.49M17.5 3.33V8.33H12.5M2.5 16.67V11.67H7.5"
                stroke="currentColor"
                stroke-width="1.67"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Replace
          </StandardButton>
          <StandardButton
            variant="outline"
            size="small"
            data-testid="ps-remove-photo"
            @click="handleRemovePhoto"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M13.33 5V4.33C13.33 3.4 13.33 2.93 13.15 2.58C12.99 2.26 12.74 2.01 12.42 1.85C12.07 1.67 11.6 1.67 10.67 1.67H9.33C8.4 1.67 7.93 1.67 7.58 1.85C7.26 2.01 7.01 2.26 6.85 2.58C6.67 2.93 6.67 3.4 6.67 4.33V5M8.33 9.58V13.75M11.67 9.58V13.75M2.5 5H17.5M15.83 5V14.33C15.83 15.73 15.83 16.43 15.56 16.97C15.32 17.44 14.94 17.82 14.47 18.06C13.93 18.33 13.23 18.33 11.83 18.33H8.17C6.77 18.33 6.07 18.33 5.53 18.06C5.06 17.82 4.68 17.44 4.44 16.97C4.17 16.43 4.17 15.73 4.17 14.33V5"
                stroke="currentColor"
                stroke-width="1.67"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Remove
          </StandardButton>
        </div>

        <div
          v-else
          class="ps-photo-drop"
          role="button"
          tabindex="0"
          aria-label="Upload profile picture"
          data-testid="ps-photo-drop"
          @click="openFilePicker"
          @keydown.enter.prevent="openFilePicker"
          @keydown.space.prevent="openFilePicker"
        >
          <span class="ps-upload" data-testid="ps-upload">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M12 16V8M8 12L12 8L16 12M4 20H20"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            Upload photo
          </span>
        </div>

        <input
          ref="fileInput"
          type="file"
          accept="image/png,image/jpeg,image/jpg"
          class="ps-file-input"
          data-testid="ps-file-input"
          @change="handleFileChange"
        />

        <p class="ps-hint" data-testid="ps-photo-hint">
          Face the camera, shoulders up, plain background. JPG or PNG, up to 5 MB.
        </p>
        <p v-if="photoError" class="ps-photo-error" role="alert" data-testid="ps-photo-error">
          {{ photoError }}
        </p>
      </div>

      <div class="ps-fields">
        <div class="ps-name-row">
          <label class="ps-field">
            <span class="ps-field-label">First name</span>
            <input
              v-model="firstNameModel"
              type="text"
              class="ps-input"
              data-testid="ps-first-name"
              aria-label="First name"
            />
          </label>
          <label class="ps-field">
            <span class="ps-field-label">Last name</span>
            <input
              v-model="lastNameModel"
              type="text"
              class="ps-input"
              data-testid="ps-last-name"
              aria-label="Last name"
            />
          </label>
        </div>
        <p class="ps-hint" data-testid="ps-name-hint">
          Pre-filled from your invitation. You can correct them if needed.
        </p>
      </div>
    </div>

    <div class="ps-spacer" aria-hidden="true"></div>

    <div class="ps-actions">
      <StandardButton
        variant="primary"
        :disabled="!canContinue"
        data-testid="ps-continue"
        @click="handleContinue"
      >
        Looks good, continue
      </StandardButton>
      <StandardButton variant="text-secondary" data-testid="ps-skip" @click="handleSkip">
        Skip for now
      </StandardButton>
      <StandardButton variant="text-secondary" data-testid="ps-do-not-ask" @click="handleDoNotAsk">
        Do not ask again
      </StandardButton>
    </div>
  </section>
</template>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from "vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";

/**
 * ER-12245 — ProfileStep (MDP-621).
 *
 * "Set your profile" (always step 2 when present). First and last name arrive
 * prefilled from the invite and are editable; the photo is optional. "Looks good,
 * continue" is disabled only while a name is empty, and "Skip for now" / "Do not
 * ask again" are always available. One photo is chosen once for every flow (rule
 * 5.3); there is no control for a second employee photo (ER-12777). Presentational
 * only: the component emits the chosen action and the edited values; saving
 * follows ER-12243.
 *
 * ER-12762: aligned to the Figma "Set your profile" frame. The picked photo is a
 * 48px thumbnail beside outlined Replace / Remove buttons (the drop zone shows only
 * while there is no photo), and the live name + photo are emitted as `preview-change`
 * so the wizard can draw the badge-preview card in the shell's media slot.
 *
 * The step keeps a local preview of the file the user picked: the parent holds
 * the file until the page is saved (rule 5.4), so without a local object URL the
 * selection would never show, and Replace/Remove would stay hidden (QA
 * 2026-10-26).
 */
const props = defineProps({
  /** Subtitle under the title; the wording differs per wizard flow. */
  subtitle: {
    type: String,
    default:
      "Help us personalize your experience. Your photo appears on your readiness badges so coaches and inspectors can recognize you.",
  },
  firstName: { type: String, default: "" },
  lastName: { type: String, default: "" },
  /** Existing user/employee picture URL (prefilled when a user picture exists). */
  photoUrl: { type: String, default: "" },
});

const emit = defineEmits([
  "continue",
  "skip",
  "do-not-ask",
  "replace-photo",
  "remove-photo",
  "preview-change",
]);

const firstNameModel = ref(props.firstName);
const lastNameModel = ref(props.lastName);
const fileInput = ref(null);
/** Object URL of the file picked in this session, if any. */
const localPhotoUrl = ref("");
/** The user removed the current photo in this session. */
const photoRemoved = ref(false);
/** Inline message shown when the picked file is empty, the wrong type or unreadable. */
const photoError = ref("");

const ALLOWED_PHOTO_TYPES = ["image/png", "image/jpeg", "image/jpg"];
const PHOTO_READ_ERROR = "We couldn't read that file. Try another photo.";

/** What the step shows: the picked file first, then the stored picture. */
const displayPhoto = computed(
  () => localPhotoUrl.value || (photoRemoved.value ? "" : props.photoUrl)
);
const hasPhoto = computed(() => Boolean(displayPhoto.value));

const canContinue = computed(
  () => firstNameModel.value.trim().length > 0 && lastNameModel.value.trim().length > 0
);

watch(
  [firstNameModel, lastNameModel, displayPhoto],
  () => {
    emit("preview-change", {
      firstName: firstNameModel.value.trim(),
      lastName: lastNameModel.value.trim(),
      photoUrl: displayPhoto.value,
    });
  },
  { immediate: true }
);

function revokeLocalPhoto() {
  if (localPhotoUrl.value && typeof URL !== "undefined" && URL.revokeObjectURL) {
    URL.revokeObjectURL(localPhotoUrl.value);
  }
  localPhotoUrl.value = "";
}

function setLocalPhoto(file) {
  revokeLocalPhoto();
  photoRemoved.value = false;
  if (typeof URL !== "undefined" && typeof URL.createObjectURL === "function") {
    localPhotoUrl.value = URL.createObjectURL(file);
  }
}

function openFilePicker() {
  if (fileInput.value) {
    fileInput.value.click();
  }
}

/** A file with no type is let through: some Windows setups report none for a valid PNG/JPG. */
function hasAllowedType(file) {
  return !file.type || ALLOWED_PHOTO_TYPES.includes(file.type);
}

async function isReadable(file) {
  if (!file.size || !hasAllowedType(file)) {
    return false;
  }
  if (typeof file.slice !== "function") {
    return true;
  }
  try {
    const head = file.slice(0, 1);
    if (typeof head.arrayBuffer === "function") {
      await head.arrayBuffer();
    }
    return true;
  } catch (error) {
    return false;
  }
}

async function handleFileChange(event) {
  const file = event?.target?.files?.[0];
  // Allow picking the same file again after a change of mind.
  if (event?.target) event.target.value = "";
  if (!file) {
    return;
  }
  if (!(await isReadable(file))) {
    photoError.value = PHOTO_READ_ERROR;
    return;
  }
  photoError.value = "";
  setLocalPhoto(file);
  emit("replace-photo", file);
}

function handleRemovePhoto() {
  photoError.value = "";
  revokeLocalPhoto();
  photoRemoved.value = true;
  emit("remove-photo");
}

function handleContinue() {
  if (!canContinue.value) {
    return;
  }
  emit("continue", {
    firstName: firstNameModel.value.trim(),
    lastName: lastNameModel.value.trim(),
    photoUrl: props.photoUrl,
  });
}

function handleSkip() {
  emit("skip");
}

function handleDoNotAsk() {
  emit("do-not-ask");
}

onBeforeUnmount(revokeLocalPhoto);
</script>

<style scoped>
.ps-step {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 32px;
  width: 100%;
  height: 100%;
  padding: 0;
  background: #ffffff;
  color: #101828;
  font-family: Inter, sans-serif;
}

.ps-heading {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.ps-title {
  margin: 0;
  color: #101828;
  font-size: 36px;
  font-weight: 600;
  line-height: 44px;
}

.ps-subtitle {
  margin: 0;
  color: #475467;
  font-size: 18px;
  line-height: 28px;
}

.ps-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ps-photo {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ps-photo-label {
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.ps-photo-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ps-photo-img {
  display: block;
  flex: 0 0 48px;
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 8px;
}

.ps-photo-drop {
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  min-height: 96px;
  background: #f9fafb;
  border: 1px dashed #d0d5dd;
  border-radius: 8px;
  cursor: pointer;
}

.ps-photo-drop:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 2px;
}

.ps-upload {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: #475467;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.ps-file-input {
  display: none;
}

.ps-hint {
  margin: 0;
  color: #475467;
  font-size: 14px;
  line-height: 20px;
}

.ps-photo-error {
  margin: 0;
  color: #b42318;
  font-size: 14px;
  line-height: 20px;
}

.ps-fields {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.ps-name-row {
  display: flex;
  gap: 16px;
}

.ps-field {
  display: flex;
  flex: 1 1 0;
  flex-direction: column;
  gap: 6px;
}

.ps-field-label {
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
}

.ps-input {
  box-sizing: border-box;
  height: 44px;
  padding: 0 14px;
  background: #ffffff;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  color: #101828;
  font-family: inherit;
  font-size: 16px;
  line-height: 24px;
}

.ps-input:focus-visible {
  outline: 2px solid #7f56d9;
  outline-offset: 1px;
}

.ps-spacer {
  flex: 1 1 auto;
}

.ps-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
</style>
